import React from 'react';
import { Pagination, Skeleton } from '@mui/material';
import DetailsCard from '../DetailsCard';
import StatusPill from '../StatusPill';
import { formatReais } from '../../utils/subscriptionFormat';
import styles from './PaymentsTable.module.css';

const FILTERS = [
  { key: 'all', label: 'Todos' },
  { key: 'paid', label: 'Pagos' },
  { key: 'pending', label: 'Pendentes' },
  { key: 'failed', label: 'Falhas' },
];

const STATUS = {
  paid: { tone: 'success', text: 'Pago' },
  pending: { tone: 'warning', text: 'Pendente' },
  failed: { tone: 'error', text: 'Falhou' },
  cancelled: { tone: 'neutral', text: 'Cancelado' },
};

const dateTimeFormat = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

// Data e hora do pagamento (ou do vencimento, se ainda não pago)
const formatDateTime = (payment) => {
  const raw = payment.paid_date || payment.due_date;
  if (raw) return dateTimeFormat.format(new Date(raw));
  return payment.paid_date_formatted || payment.due_date_formatted || 'Data não disponível';
};

const SkeletonRows = () => [0, 1, 2].map((row) => (
  <tr key={row}>
    {[0, 1, 2, 3, 4].map((cell) => (
      <td key={cell}><Skeleton variant="text" width={cell === 1 ? 110 : 80} /></td>
    ))}
  </tr>
));

/** Histórico de pagamentos: filtro por situação, tabela e paginação. */
const PaymentsTable = ({ payments, pagination, onPageChange, statusFilter, onStatusFilterChange, loading }) => {
  const filters = (
    <div className={styles.filters} role="group" aria-label="Filtrar pagamentos">
      {FILTERS.map(({ key, label }) => (
        <button
          key={key}
          type="button"
          className={`${styles.filter} ${statusFilter === key ? styles.filterActive : ''}`}
          onClick={() => onStatusFilterChange(key)}
        >
          {label}
        </button>
      ))}
    </div>
  );

  const empty = !loading && (!payments || payments.length === 0);

  return (
    <DetailsCard title="Pagamentos" action={filters}>
      {empty ? (
        <p className={styles.empty}>Nenhum pagamento encontrado.</p>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Data</th>
                <th>Plano</th>
                <th>Valor</th>
                <th>Situação</th>
                <th className={styles.alignRight}>Recibo</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <SkeletonRows /> : payments.map((payment) => {
                const status = STATUS[payment.status?.toLowerCase()]
                  || { tone: 'neutral', text: payment.status || 'Desconhecido' };
                return (
                  <tr key={payment.id}>
                    <td className={styles.date}>{formatDateTime(payment)}</td>
                    <td>{payment.plan_name}</td>
                    <td className={styles.amount}>{formatReais(payment.amount)}</td>
                    <td><StatusPill tone={status.tone}>{status.text}</StatusPill></td>
                    <td className={styles.alignRight}>
                      {payment.invoice_url ? (
                        <a href={payment.invoice_url} target="_blank" rel="noopener noreferrer" className={styles.link}>
                          Ver recibo
                        </a>
                      ) : (
                        <span className={styles.muted}>Indisponível</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {pagination && pagination.total_pages > 1 && (
        <div className={styles.pagination}>
          <span>Página {pagination.page} de {pagination.total_pages}</span>
          <Pagination
            count={pagination.total_pages}
            page={pagination.page}
            onChange={(e, page) => onPageChange(page)}
            color="primary"
            size="small"
          />
        </div>
      )}
    </DetailsCard>
  );
};

export default PaymentsTable;
